import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams, useNavigate, type NavigateFunction } from 'react-router-dom';
import { newIdempotencyKey } from '@/features/wallet/services/walletService';
import AdminTabs from '@/features/admin/components/AdminTabs';
import Modal from '@/features/admin/components/AdminModal';
import UserSearchSelect from '@/features/admin/components/UserSearchSelect';
import { DataTable } from '@/shared/ui/DataTable';
import { createDataTableColumns } from '@/shared/ui/dataTableFeatures';
import { usePaginatedList } from '@/shared/hooks/usePaginatedList';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { safeErrorMessage } from '@/shared/utils/errors';
import { createUser, grantCredit, searchUsers, updateUser } from '@/features/admin/services/adminService';
import type { AdminUser } from '@/shared/types';

const PAGE_SIZE = 200;

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const usersColumnHelper = createDataTableColumns<AdminUser>();

function buildUsersColumns(
  navigate: NavigateFunction,
  onGrant: (u: AdminUser) => void,
  onEdit: (u: AdminUser) => void,
) {
  return usersColumnHelper.columns([
    usersColumnHelper.accessor((u) => formatDate(u.createdAt), {
      id: 'createdAt',
      header: 'Signed up',
      filterFn: 'includesString',
    }),
    usersColumnHelper.accessor('email', {
      header: 'Email',
      filterFn: 'includesString',
    }),
    usersColumnHelper.accessor('name', {
      header: 'Name',
      enableSorting: false,
      filterFn: 'includesString',
      cell: (info) => info.getValue() || '—',
    }),
    usersColumnHelper.accessor('phoneNumber', {
      header: 'Phone',
      enableSorting: false,
      filterFn: 'includesString',
      cell: (info) => info.getValue() || '—',
    }),
    usersColumnHelper.accessor((u) => u.credits, {
      id: 'credits',
      header: 'Balance',
      enableColumnFilter: false,
      meta: { filterType: 'none' },
      cell: (info) => {
        const val = info.getValue();
        if (val === undefined || val === null) return '—';
        const num = typeof val === 'number' ? val : Number(val);
        return Number.isNaN(num) ? '—' : `$${num.toFixed(2)}`;
      },
    }),
    usersColumnHelper.accessor('role', {
      header: 'Role',
      filterFn: 'equals',
      meta: {
        filterType: 'select',
        filterOptions: [
          { label: 'User', value: 'user' },
          { label: 'Admin', value: 'admin' },
        ],
      },
    }),
    usersColumnHelper.display({
      id: 'actions',
      header: 'Actions',
      enableColumnFilter: false,
      meta: { filterType: 'none' },
      cell: ({ row }) => {
        const u = row.original;
        return (
          <div className="admin-table-actions">
            <button
              type="button"
              className="admin-action-btn"
              onClick={() => navigate(`/admin/users/${u.id}/jobs`, { state: { email: u.email } })}
              title="View video jobs"
              aria-label="View video jobs"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                <line x1="7" y1="2" x2="7" y2="22" />
                <line x1="17" y1="2" x2="17" y2="22" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <line x1="2" y1="7" x2="7" y2="7" />
                <line x1="2" y1="17" x2="7" y2="17" />
                <line x1="17" y1="17" x2="22" y2="17" />
                <line x1="17" y1="7" x2="22" y2="7" />
              </svg>
            </button>
            <button
              type="button"
              className="admin-action-btn"
              onClick={() => onGrant(u)}
              title="Grant credit"
              aria-label="Grant credit"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M16 8h-6a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8" />
                <path d="M12 6v12" />
              </svg>
            </button>
            <button
              type="button"
              className="admin-action-btn"
              onClick={() => onEdit(u)}
              title="Edit user"
              aria-label="Edit user"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
            </button>
          </div>
        );
      },
    }),
  ]);
}

// --- Create User modal -------------------------------------------------------

function CreateUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: (u: AdminUser) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const user = await createUser({
        email: email.trim(),
        name: name.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
      });
      onCreated(user);
    } catch (err) {
      setError(safeErrorMessage(err, 'Unable to create this account.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Create user"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="create-user-form" className="button" disabled={saving}>
            {saving ? 'Creating…' : 'Create account'}
          </button>
        </>
      }
    >
      <form id="create-user-form" onSubmit={onSubmit}>
        <div className="form-field">
          <label className="field-label" htmlFor="cu-email">
            Email
          </label>
          <input
            id="cu-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
          />
        </div>
        <div className="form-field">
          <label className="field-label" htmlFor="cu-name">
            Name (optional)
          </label>
          <input id="cu-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="field-label" htmlFor="cu-phone">
            Phone number (optional)
          </label>
          <input
            id="cu-phone"
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </div>
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      </form>
    </Modal>
  );
}

// --- Grant Credit modal -------------------------------------------------------

function GrantCreditModal({
  initialUser,
  onClose,
  onGranted,
}: {
  initialUser: AdminUser;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [selected, setSelected] = useState<AdminUser | null>(initialUser);
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fresh per modal open — stable across a double-click on the same open modal
  // (guards the credit-grant endpoint's idempotency_key).
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const applyPreset = (presetAmount: number, presetLabel: string) => {
    setAmount(String(presetAmount));
    setLabel(presetLabel);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const amountCredits = Number(amount);
    if (!selected) {
      setError('Choose a user to grant credit to.');
      return;
    }
    if (!(amountCredits > 0) || !label.trim()) {
      setError('Amount must be positive and a label is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await grantCredit(selected.id, {
        amountCredits,
        label: label.trim(),
        reason: reason.trim() || undefined,
        idempotencyKey,
      });
      onGranted();
    } catch (err) {
      setError(safeErrorMessage(err, 'Unable to record this grant.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Grant credit"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="grant-credit-form" className="button" disabled={saving}>
            {saving ? 'Granting…' : 'Grant credit'}
          </button>
        </>
      }
    >
      <form id="grant-credit-form" onSubmit={onSubmit}>
        <div className="form-field">
          <label className="field-label" htmlFor="gc-user">
            User
          </label>
          <UserSearchSelect initialUser={initialUser} onSelect={setSelected} />
        </div>

        <div className="form-field admin-search-form__row">
          <button type="button" className="button secondary" onClick={() => applyPreset(10, 'Welcome Credit')}>
            Welcome Credit · 10
          </button>
          <button type="button" className="button secondary" onClick={() => applyPreset(5, 'Bonus Credit')}>
            Bonus Credit · 5
          </button>
        </div>

        <div className="form-field">
          <label className="field-label" htmlFor="gc-amount">
            Amount (credits)
          </label>
          <input
            id="gc-amount"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="100"
          />
        </div>
        <div className="form-field">
          <label className="field-label" htmlFor="gc-label">
            Label (shown to the user)
          </label>
          <input
            id="gc-label"
            type="text"
            maxLength={80}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Welcome Credit"
          />
        </div>
        <div className="form-field">
          <label className="field-label" htmlFor="gc-reason">
            Internal reason (optional — never shown to the user)
          </label>
          <textarea
            id="gc-reason"
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. requested via support ticket #123"
          />
        </div>
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      </form>
    </Modal>
  );
}

// --- Edit User modal ----------------------------------------------------------

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: (u: AdminUser) => void;
}) {
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email);
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber || '');
  const [role, setRole] = useState<'user' | 'admin'>(user.role);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const updated = await updateUser(user.id, {
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        role,
      });
      onSaved(updated);
    } catch (err) {
      setError(safeErrorMessage(err, 'Unable to save these changes.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit user"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-user-form" className="button" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <form id="edit-user-form" onSubmit={onSubmit}>
        <div className="form-field">
          <label className="field-label" htmlFor="eu-name">
            Name
          </label>
          <input id="eu-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="field-label" htmlFor="eu-email">
            Email
          </label>
          <input id="eu-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="field-label" htmlFor="eu-phone">
            Phone number
          </label>
          <input
            id="eu-phone"
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label className="field-label" htmlFor="eu-role">
            Role
          </label>
          <select id="eu-role" value={role} onChange={(e) => setRole(e.target.value as 'user' | 'admin')}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      </form>
    </Modal>
  );
}

// --- Page ----------------------------------------------------------------------

export default function AdminUsersPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { items: users, offset, loading, loadingMore, error, setError, hasMore, load, loadMore } = usePaginatedList<AdminUser>(
    (nextOffset) =>
      searchUsers({
        limit: PAGE_SIZE,
        offset: nextOffset,
      }).then((page) => ({ items: page.users, total: page.total, offset: page.offset })),
  );
  const [notice, setNotice] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [grantTarget, setGrantTarget] = useState<AdminUser | null>(null);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);

  const columns = useMemo(
    () => buildUsersColumns(navigate, setGrantTarget, setEditTarget),
    [navigate],
  );

  useEffect(() => {
    load(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const query = searchParams.get('q') || '';
  const initialColumnFilters = useMemo(() => {
    if (!query) return [];
    return [{ id: 'email', value: query }];
  }, [query]);

  return (
    <section className="admin-users">
      <AdminTabs />

      <div className="admin-page-head">
        <div className="admin-header">
          <h1>Users</h1>
        </div>
        <button type="button" className="button" onClick={() => setShowCreate(true)}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" />
          </svg>
          Create user
        </button>
      </div>

      {notice && (
        <div className="empty-state admin-section-gap-sm" style={{ marginBottom: '1rem' }}>
          <div className="empty-state__title">{notice}</div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      ) : error ? (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      ) : users.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No users found</div>
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={users}
            getRowId={(u) => u.id}
            initialColumnFilters={initialColumnFilters}
            enablePagination={true}
            pageSize={20}
          />
          {hasMore && (
            <button
              type="button"
              className="button secondary wallet-activity__more"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load more from server'}
            </button>
          )}
        </>
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={(user) => {
            setShowCreate(false);
            setNotice(`Created ${user.email}.`);
            load(0, false);
          }}
        />
      )}

      {grantTarget && (
        <GrantCreditModal
          initialUser={grantTarget}
          onClose={() => setGrantTarget(null)}
          onGranted={() => {
            setNotice(`Credit granted to ${grantTarget.email}.`);
            setGrantTarget(null);
          }}
        />
      )}

      {editTarget && (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setNotice(`Saved changes to ${editTarget.email}.`);
            setEditTarget(null);
            load(offset, false);
          }}
        />
      )}
    </section>
  );
}
